// The top page's one step onto the tour that is not a cube (P1): a small tag
// beside the page's heading, "The tour now · <space>", to /hypertour/. The space is the
// one the tour says it is in (`tourstate` in the `hyper-tour` room, as
// ask-the-room.js reads it), kept current while the page is open; with no
// guide answering, the tag is not shown — there is no tour to point at.
//
// A cube in the Tower would be a change to the world (its placement and the
// Tower's route); this is the page's own layer, like the menu. Hidden while
// the tab is on the tour (tour-nav.js folds the page). Its place and look are
// provisional.
//
// What it hears it also says, as `hyper-tour:space { space }` on window
// whenever the space changes (null when the tour falls quiet), and keeps as
// `window.hyperTourSpace`: the page's herder is in the Tower's room only
// while the tour is there (pigeon-herder `listensWhile`).
import { Pigeon } from '../theater/Pigeon.js';
import { pigeonRoomUrl } from '../theater/variables.js';
import { SPACE_NAMES } from './space-names.js';
import { receive } from './tour-party.js';

// This page's way onto the tour is the tag: the row of faces is not offered
// beside it (tour-nav.js).
document.documentElement.dataset.tourTag = '';

const TOUR_ROOM_ADDRESS = 'hyper-tour';
// No tourstate for this long and the tour is taken to have stopped.
const QUIET_MS = 15000;
const SPACES = SPACE_NAMES;

function build() {
  const style = document.createElement( 'style' );
  style.textContent = `
    .tour-tag {
      position: fixed; z-index: 100; display: none; align-items: center; gap: 8px;
      left: 16px; top: calc(56px + env(safe-area-inset-top));
      padding: 6px 12px; border-radius: 16px; text-decoration: none;
      background: rgba(0, 0, 0, 0.55); color: #fff; font: 13px/1.4 sans-serif;
      backdrop-filter: blur(6px); -webkit-backdrop-filter: blur(6px);
    }
    .tour-tag::before {
      content: ""; width: 8px; height: 8px; border-radius: 50%; background: #ff7a1a;
      animation: tour-tag-beat 2.4s ease-in-out infinite;
    }
    @keyframes tour-tag-beat { 0%, 100% { opacity: 1; } 50% { opacity: 0.3; } }
    .tour-tag.live { display: inline-flex; }
    html.on-tour .tour-tag { display: none !important; }
    /* Beside the page's heading where it has one (the top page's #title),
       on its line and after it: the heading keeps its one line, and the tag
       gives way, short of the menu button, before it would cross it. Provisional: its place is to become the row of faces'
       (tour/tour-row.js) once that row is the way in off the tour too. */
    #title.has-tour-tag {
      display: flex; align-items: center; gap: 12px; max-width: calc(100vw - 24px - 64px);
    }
    #title.has-tour-tag h1 { flex: none; white-space: nowrap; }
    /* The tag is 30 px tall and the heading 18: the tag's extra height is
       taken back in its margins, so the row stays the heading's and the
       heading stands where it stands without the tag, centred on the menu
       button. */
    #title .tour-tag { position: static; min-width: 0; margin: -6px 0; }
    #title .tour-tag > span { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  `;
  document.head.appendChild( style );
  const tag = document.createElement( 'a' );
  tag.className = 'tour-tag';
  tag.href = '/hypertour/';
  tag.innerHTML = '<span lang="ja">開催中のツアー · <b></b></span><span lang="en">The tour now · <b></b></span>';
  const lang = document.querySelector( 'span[lang="ja"][style*="none"]' ) ? 'en' : 'ja';
  for ( const span of tag.querySelectorAll( 'span[lang]' ) ) {
    if ( span.getAttribute( 'lang' ) !== lang ) span.style.display = 'none';
  }
  const title = document.getElementById( 'title' );
  if ( title ) {
    title.classList.add( 'has-tour-tag' );
    title.appendChild( tag );
  } else {
    document.body.appendChild( tag );
  }
  return tag;
}

function start() {
  const endpoint = pigeonRoomUrl[ 1 ];
  if ( !endpoint ) return;
  const tag = build();
  let quiet = null;
  let told;
  const tell = ( space ) => {
    if ( space === told ) return;
    told = space;
    // Kept as well as said: a listener that starts after the word went out
    // (the page's herder, built with its scene) reads where the tour is now.
    window.hyperTourSpace = space;
    window.dispatchEvent( new CustomEvent( 'hyper-tour:space', { detail: { space } } ) );
  };
  const say = ( space ) => {
    tell( space || null );
    const names = SPACES[ space ];
    if ( !names ) return;
    const [ ja, en ] = tag.querySelectorAll( 'b' );
    ja.textContent = names.ja;
    en.textContent = names.en;
    tag.classList.add( 'live' );
    clearTimeout( quiet );
    quiet = setTimeout( () => { tag.classList.remove( 'live' ); tell( null ); }, QUIET_MS );
  };
  try {
    const pigeon = new Pigeon( endpoint, TOUR_ROOM_ADDRESS, null );
    pigeon.onMessage( ( msg ) => {
      if ( !msg || msg.type !== 'tourstate' || !msg.body ) return;
      // Who is on the tour, for every view in the tab (tour-party): the top
      // page hears the tour's own room only here.
      receive( msg.body );
      const at = typeof msg.body.at === 'string' ? msg.body.at.split( '/' )[ 0 ] : null;
      say( at || msg.body.space );
    } );
  } catch ( _err ) {
    // No room to hear: no tag.
  }
}

if ( document.readyState === 'loading' ) document.addEventListener( 'DOMContentLoaded', start );
else start();
